import { Component, OnDestroy, AfterViewInit } from '@angular/core';
import { User, Enterprise } from '@core/store';
import { Store, select } from '@ngrx/store';
import { filter, takeUntil } from 'rxjs/operators';
import format from 'date-fns/format';
import forEachRight from 'lodash/forEachRight';
import { Subscription, Subject, timer } from 'rxjs';
import getMinutes from 'date-fns/getMinutes';
import { WaterMark } from './watermark.type';

@Component({
  selector: 'layout-watermark',
  template: ``
})
export class WatermarkComponent implements AfterViewInit, OnDestroy {
  private readonly fontSize = 16; // 字体大小
  private readonly angleNumber = (1 / 16) * Math.PI; // 偏转角度
  private readonly rowHeight = 20; // 行高
  private readonly enlargeWeight = 1.2; // 水印区域放大倍数
  private readonly lightOpacity = 0.08; // 明水印透明度
  private readonly darkOpacity = 0.005; // 暗水印透明度

  private user: User;
  private enterprise: Enterprise;
  private watermark: WaterMark;
  private context: CanvasRenderingContext2D;
  private destroy$: Subscription;
  private destroySub$: Subject<void> = new Subject<void>();

  private observer: MutationObserver = new MutationObserver(mutationsList => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        mutation.removedNodes.forEach(node => {
          if (node.nodeName === 'CANVAS') {
            this.initCanvas();
            this.drawWaterMark();
            this.canvasObserver.disconnect();
            this.canvasObserver.observe(this.context.canvas, { attributes: true, attributeFilter: ['style', 'class'] });
          }
        });
      }
    }
  });
  private canvasObserver: MutationObserver = new MutationObserver(mutationsList => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'attributes') {
        if (['style', 'class'].includes(mutation.attributeName)) {
          this.drawCanvasStyle(mutation.target as HTMLCanvasElement);
        }
      }
    }
  });

  constructor(private readonly store: Store<{ user: User; enterprise: Enterprise; watermark: WaterMark }>) {}

  ngAfterViewInit(): void {
    this.initCanvas();
    this.getData();

    let lastMinute: number;
    this.destroy$ = timer(0, 1000).subscribe(() => {
      const currentMinute = getMinutes(new Date());
      if (lastMinute != currentMinute) {
        this.drawWaterMark();
        lastMinute = currentMinute;
      }
    });
  }

  ngOnDestroy() {
    this.observer.disconnect();
    this.destroy$?.unsubscribe();
    this.destroySub$.next();
    this.destroySub$.complete();
  }

  private getData(): void {
    this.store
      .pipe(
        select(state => state.user),
        filter(user => !!user),
        takeUntil(this.destroySub$)
      )
      .subscribe((user: User) => {
        this.user = user;
      });

    this.store
      .pipe(
        select(state => state.enterprise),
        filter(enterprise => !!enterprise),
        takeUntil(this.destroySub$)
      )
      .subscribe((enterprise: Enterprise) => {
        this.enterprise = enterprise;
      });

    this.store
      .pipe(
        select(state => state.watermark),
        filter(watermark => !!watermark),
        takeUntil(this.destroySub$)
      )
      .subscribe((watermark: WaterMark) => {
        this.watermark = watermark;
        if (Object.getOwnPropertyNames(this.watermark).length !== 0) {
          this.drawWaterMark();
        }
      });
  }

  private initCanvas() {
    const watermark = document.createElement('canvas');
    this.drawCanvasStyle(watermark);

    this.context = watermark.getContext('2d');
    const mainContent = document.querySelector('.right-content');
    mainContent.prepend(watermark);

    this.observer.disconnect();
    this.observer.observe(mainContent, { childList: true });
  }

  private drawCanvasStyle(canvas: HTMLCanvasElement) {
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '9999999';
  }

  private drawWaterMark() {
    // 文字详细内容
    const markText = this.formatTextArray();
    if (markText.length <= 0) {
      return;
    }

    const contextDetail = document.createElement('canvas').getContext('2d');
    // 计算最大宽度
    const maxWidth = Math.max(
      ...markText.map(val => {
        this.formatDetailFont(contextDetail);
        const valWidth = contextDetail.measureText(val);
        return valWidth.width;
      })
    );

    // 计算图区域
    contextDetail.canvas.width =
      (maxWidth + this.rowHeight + Math.sin(this.angleNumber) * this.rowHeight) * this.enlargeWeight;
    contextDetail.canvas.height =
      (Math.sin(this.angleNumber) * maxWidth + (this.rowHeight * markText.length) / Math.cos(this.angleNumber)) *
      this.enlargeWeight;

    // 写入水印
    contextDetail.clearRect(0, 0, contextDetail.canvas.width, contextDetail.canvas.height);
    const isLight = this.watermark?.showMode === 'plaintext';
    contextDetail.fillStyle = 'rgba(0, 0, 0, ' + (isLight ? this.lightOpacity : this.darkOpacity) + ')';

    this.formatDetailFont(contextDetail);
    forEachRight(markText, (text, index) => {
      contextDetail.fillText(
        text,
        0,
        contextDetail.canvas.height - index * this.rowHeight * Math.cos(this.angleNumber)
      );
    });

    // 重复添加水印到屏幕
    this.context.canvas.width = window.innerWidth;
    this.context.canvas.height = window.innerHeight;
    this.context.clearRect(0, 0, this.context.canvas.width, this.context.canvas.height);
    this.context.fillStyle = this.context.createPattern(contextDetail.canvas, 'repeat');
    this.context.fillRect(0, 0, this.context.canvas.width, this.context.canvas.height);

    // 删除
    contextDetail.canvas.remove();
  }

  private formatTextArray(): string[] {
    let arr = [];
    let date = format(new Date(), 'yyyy.MM.dd HH:mm');
    let name = this.user.nickname || this.user.userLogin;
    if (this.watermark?.includeContent.includes('currentTime')) {
      if (this.watermark?.includeContent.includes('userName')) {
        arr.push(name + ' ' + date);
      } else {
        arr.push(date);
      }
    } else {
      if (this.watermark?.includeContent.includes('userName')) {
        arr.push(name);
      }
    }

    if (this.watermark?.includeContent.includes('groupName')) {
      arr.push(this.enterprise.enterpriseName);
    }
    return arr;
  }

  private formatDetailFont(contextDetail: CanvasRenderingContext2D) {
    contextDetail.font = contextDetail.font.replace(/\d+px/, `${this.fontSize}px`);
    contextDetail.rotate(-this.angleNumber);
  }
}
